/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useCallback, useMemo } from "react";

import { selectIsTvDevice } from "../../../../viewModel/device";
import {
  useUnityDispatch,
  useUnitySelector,
  UnityAppStore
} from "../../../../viewModel";
import {
  selectIsActiveOptionVisible,
  selectActiveOptionFocusIndex,
  RecordingOption,
  selectActiveRecordingOption,
  setActiveOptionFocusIndex,
  confirmActiveOptionValue,
  closeOptionValue,
  RecordingOptionListType
} from "../../../../viewModel/myHub/seriesRecordingOptions";
import { useModalController } from "../../../hooks";
import HorizontalSelectionDialog from "./HorizontalSelectionDialog";
import { HorizontalSelectionDialogController } from "./HorizontalSelectionDialogController";
import { selectActiveOptionDescription } from "../../../../viewModel/myHub/seriesRecordingOptions/seriesRecordingOptionsSlice";

/**
 * Connected container for the HorizontalSelectionDialog sub-panel.
 *
 * Renders when a Series Recording option picker is open (`selectIsPickerVisible`).
 * Pushes a HorizontalSelectionDialogController onto the modal stack to consume
 * D-pad input while visible.
 */
const HorizontalSelectionDialogContainer: FunctionComponent =
  (): JSX.Element | null => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
    const TAG: string = "HorizontalSelectionDialogContainer";
    const dispatch = useUnityDispatch();

    const isVisible: boolean = useUnitySelector(selectIsActiveOptionVisible);
    const focusIndex: number = useUnitySelector(selectActiveOptionFocusIndex);
    const activeOption: RecordingOption | undefined = useUnitySelector(
      selectActiveRecordingOption
    );
    const description: string = useUnitySelector(selectActiveOptionDescription);

    const modalController = useMemo(
      (): HorizontalSelectionDialogController =>
        new HorizontalSelectionDialogController(
          dispatch,
          UnityAppStore.getState
        ),
      [dispatch]
    );

    const showCloseButton: boolean = !selectIsTvDevice();

    useModalController(modalController, isVisible);

    const handleSelect = useCallback(
      (index: number): void => {
        dispatch(setActiveOptionFocusIndex(index));
        dispatch(confirmActiveOptionValue());
      },
      [dispatch]
    );

    const handleBack = useCallback((): void => {
      dispatch(closeOptionValue());
    }, [dispatch]);

    const handleCloseHover = useCallback((): void => {
      dispatch(setActiveOptionFocusIndex(-1));
    }, [dispatch]);

    if (
      isVisible !== true ||
      activeOption === undefined ||
      activeOption.horizontalOptions === undefined ||
      activeOption.horizontalOptions.length === 0
    ) {
      return null;
    }

    const displayOptions: string[] =
      activeOption.optionListType === RecordingOptionListType.KeepAtMost &&
      activeOption.displayOptions !== undefined &&
      activeOption.displayOptions.length > 0
        ? activeOption.displayOptions
        : activeOption.horizontalOptions;

    const isCompactOption = (
      optionListType: RecordingOptionListType
    ): boolean => {
      return (
        optionListType === RecordingOptionListType.KeepAtMost ||
        optionListType === RecordingOptionListType.KeepUntil
      );
    };

    return (
      <HorizontalSelectionDialog
        title={activeOption.label}
        onCloseHover={handleCloseHover}
        description={description}
        options={displayOptions}
        focusIndex={focusIndex}
        onSelect={handleSelect}
        onBack={handleBack}
        showCloseButton={showCloseButton}
        isCompactOption={isCompactOption(activeOption.optionListType)}
      />
    );
  };

export default HorizontalSelectionDialogContainer;
